/**
 * Host console — the facilitator's view of one session.
 *
 * The sequence mirrors the canonical facilitator flow: open for participants,
 * share the link, watch submissions, close & reveal, publish. Every action is
 * a canonical store/RPC call (sessionAppActions).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Check, Copy, ExternalLink, Tv } from 'lucide-react';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useToolSessionStore } from '@/stores/prioritization/toolSessionStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton, SecondaryButton, StatusChip } from '../components/primitives';
import {
  closeAndComputeResults,
  openForParticipants,
  publishResults,
  sessionAppResultGroups,
  sessionAppResultCsv,
  sessionAppResultRows,
  sessionAppToolName,
  sessionToolItems,
} from './sessionAppActions';
import { sessionStateLabel } from '@/lib/sessions/vocabulary';
import { GuestLinkControl } from '@/components/group/GuestLinkControl';
import { CsvExportButton } from '@/components/ui/ExportMenu';
import { loadSessionPrices, toEffortAttribute } from '@/lib/buyAFeaturePricing';
import { normalizeSessionToolId, toolAsksForCriteria } from '@/lib/sessionToolCatalog';
import { readToolSettings, scoringScaleLabel } from '@/lib/toolSettings';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { RevealOrchestrator } from '@/features/sessions-participant/components/results/RevealOrchestrator';
import { TiePanel } from '@/components/prioritization/RankOverridePanel';
import { readTies, sharedRanks } from '@/lib/sessions/rankTies';
import { useSessionRankChangeRule } from '@/lib/sessions/rankChangeRule';
import { formatRank } from '@/lib/itemRankings';
import { useSessionPublishRight } from '@/hooks/prioritization/useActRight';
import { useSessionPublishAuthority } from '@/hooks/prioritization/useSessionPublishAuthority';
import { refusedByRight } from '@/lib/actRight';

/** Host console is shared by Session and Rank — both are S5 ranking surfaces. */
function appBaseFromPath(pathname: string): { appId: 'session' | 'rank'; basePath: string } {
  if (pathname.startsWith('/apps/rank')) return { appId: 'rank', basePath: '/apps/rank' };
  return { appId: 'session', basePath: '/apps/session' };
}

export default function HostScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { appId, basePath } = appBaseFromPath(location.pathname);
  const app = getAppDefinition(appId)!;
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);

  const fetchSessionById = useSessionStore((s) => s.fetchSessionById);
  const session = useSessionStore((s) => s.sessions.find((x) => x.id === sessionId));
  // 1001: the cycle's decision right, as publish_session_outcome will judge it —
  // asked only when the session can produce a Priority List.
  const publishRight = useSessionPublishRight(session?.cycleId && session?.trackId ? session.id : null);
  // Open (transition_session_state), Close (run_session) and Publish
  // (publish_session_results) share one server predicate,
  // internal_session_publish_authority: the facilitator, the creator, the cycle
  // owner, a workspace admin, or a holder of session.publish / session.facilitate.
  // The console is drawn for exactly those people (build contract §2.8).
  const mayHost = useSessionPublishAuthority(session);

  const subscribeToSessionToolSessions = useToolSessionStore((s) => s.subscribeToSessionToolSessions);
  const toolSessions = useToolSessionStore((s) => s.toolSessions);
  const participantResponses = useToolSessionStore((s) => s.participantResponses);

  // Which act is running, so only its own button says so (one flag made Publish
  // read "Publishing…" while the session was still closing).
  const [busyAct, setBusyAct] = useState<'open' | 'close' | 'publish' | null>(null);
  const busy = busyAct !== null;
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (sessionId) void fetchSessionById(sessionId);
  }, [sessionId, fetchSessionById]);

  useEffect(() => {
    if (sessionId && session?.state === 'CRITERIA_FINALIZED') {
      void subscribeToSessionToolSessions(sessionId);
    }
  }, [sessionId, session?.state, subscribeToSessionToolSessions]);

  // Buy-a-Feature: the items' prices, the same ones the room was shown, so the
  // result can say what the room pledged against each and what it funded.
  const effortDefinition = useAttributesStore((state) => state.getAttributeByKey('effort'));
  const isBuyAFeature = normalizeSessionToolId(session?.selectedToolId) === 'buy_a_feature';
  const hasResult = Boolean(session?.rankingOutput?.rankedItemIds?.length);
  const [prices, setPrices] = useState<Record<string, number> | null>(null);
  // S-5: ties, and whether this host may break them (the session's facilitator may).
  const { rule: rankRule, reload: reloadRankRule } = useSessionRankChangeRule(sessionId, session?.state);
  useEffect(() => {
    if (!session || !isBuyAFeature || !hasResult) return;
    let cancelled = false;
    const ids = sessionToolItems(session).map((item) => item.id);
    void loadSessionPrices(ids, session.id, toEffortAttribute(effortDefinition)).then((loaded) => {
      if (!cancelled) setPrices(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [session, isBuyAFeature, hasResult, effortDefinition]);

  const activeToolSessionIds = useMemo(
    () => toolSessions.filter((t) => t.sessionId === sessionId).map((t) => t.id),
    [toolSessions, sessionId]
  );
  const submittedCount = useMemo(
    () =>
      new Set(
        participantResponses
          .filter((r) => activeToolSessionIds.includes(r.toolSessionId) && r.isComplete)
          .map((r) => r.participantId)
      ).size,
    [participantResponses, activeToolSessionIds]
  );

  const joinUrl = `${window.location.origin}${basePath}/${sessionId}`;

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — long-press or select the link text instead.');
    }
  }, [joinUrl]);

  const run = useCallback(
    async (act: 'open' | 'close' | 'publish', action: () => Promise<{ success: boolean; error?: string }>) => {
      setBusyAct(act);
      setError(null);
      const result = await action();
      setBusyAct(null);
      // A refusal without words is a cancelled confirmation, not a failure.
      if (!result.success && result.error) setError(result.error);
    },
    []
  );

  if (!session) {
    return (
      <AppShell app={app} backTo={basePath}>
        <EmptyState title="Loading session…" />
      </AppShell>
    );
  }

  if (!mayHost) {
    // Not your console: the server would refuse every act on it. Go participate
    // instead. <Navigate>, not navigate(): React Router drops a navigate() called
    // during the first render, which left the page blank when the session was
    // already loaded.
    return <Navigate to={`${basePath}/${session.id}`} replace />;
  }

  const items = sessionToolItems(session);
  // The saved order, with each option's average score (score methods); sorting
  // methods show the groups themselves instead.
  const resultRows = sessionAppResultRows(session, items, prices);
  const resultGroups = sessionAppResultGroups(session, items);
  const rankedIds = session.rankingOutput?.rankedItemIds ?? [];
  const ties = readTies((session.rankingOutput as { ties?: unknown } | undefined)?.ties);
  const ranks = sharedRanks(rankedIds, ties);
  const nameOf = (id: string) => items.find((item) => item.id === id)?.name ?? id;

  const stateLabel = sessionStateLabel(session.state);
  // The scale chosen at setup, for a method that scores options against criteria.
  const scaleNote = toolAsksForCriteria(normalizeSessionToolId(session.selectedToolId))
    ? ` · scored ${scoringScaleLabel(readToolSettings(session.toolConfig).scoringScale)}`
    : '';

  const ballWith =
    session.state === 'DRAFT'
      ? 'you — open the session when you are ready'
      : session.state === 'CRITERIA_FINALIZED'
        ? 'the participants — they are weighing in now'
        : session.state === 'PUBLISHED'
          ? 'nobody — the result is published'
          : 'you — review and publish the result';

  return (
    <AppShell app={app} backTo={basePath} backLabel={appId === 'rank' ? 'Back to Rank' : 'Back to your sessions'}>
      <ScreenHeading title={session.name} subtitle={`${sessionAppToolName(session.selectedToolId)} · ${items.length} options${scaleNote}`} />
      <div className="mb-4 flex items-center gap-2">
        <StatusChip
          label={stateLabel}
          tone={session.state === 'CRITERIA_FINALIZED' ? 'mustard' : session.state === 'DRAFT' ? 'neutral' : 'green'}
        />
      </div>
      <BallLine ballWith={ballWith} />

      <div className="space-y-4">
        {session.state === 'DRAFT' && mayHost ? (
          <AppCard>
            <Eyebrow>Open for participants</Eyebrow>
            <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
              Opening locks the setup and turns on the join link. Anyone with the
              link weighs in from their own device, at their own pace.
            </p>
            <div className="mt-4">
              <PrimaryButton
                disabled={busy}
                onClick={() =>
                  void run('open', () => openForParticipants(session, userId ?? '', userName ?? 'Facilitator'))
                }
              >
                {busyAct === 'open' ? 'Opening…' : 'Open the session'}
              </PrimaryButton>
            </div>
          </AppCard>
        ) : null}

        {session.state !== 'DRAFT' ? (
          <AppCard>
            <Eyebrow>Invite</Eyebrow>
            <p className="mt-2 break-all rounded-md bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]">
              {joinUrl}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <SecondaryButton onClick={() => void copyLink()}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy link'}
              </SecondaryButton>
              <SecondaryButton onClick={() => window.open(joinUrl, '_blank')}>
                <ExternalLink className="h-4 w-4" /> Open as participant
              </SecondaryButton>
              {/* Participants are sent to the product's live board (SessionRoomRouter);
                  the host sees the same room, with its roster, spotlight, broadcast and Board. */}
              <SecondaryButton onClick={() => navigate(`/prioritization/sessions/${session.id}/live`)}>
                <Tv className="h-4 w-4" /> Open the room
              </SecondaryButton>
            </div>
            {/* People without an account join by the guest link, with a name only (ruling R6). */}
            <div className="mt-4 border-t border-gray-200 pt-4 dark:border-[var(--rule)]" data-testid="session-guest-link">
              <GuestLinkControl sessionId={session.id} />
            </div>
          </AppCard>
        ) : null}

        {session.state === 'CRITERIA_FINALIZED' && mayHost ? (
          <AppCard>
            <Eyebrow>Progress</Eyebrow>
            <p className="mt-2 font-heading text-2xl font-semibold text-gray-900 dark:text-[var(--text-pri)]">
              {submittedCount}
              <span className="ml-2 align-middle font-body text-sm font-normal text-gray-500 dark:text-[var(--text-ter)]">
                submission{submittedCount === 1 ? '' : 's'} so far
              </span>
            </p>
            <p className="mt-1 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              Close when enough people have weighed in — the result is computed
              from everyone who submitted.
            </p>
            <div className="mt-4">
              <PrimaryButton
                disabled={busy || submittedCount === 0}
                onClick={() => void run('close', () => closeAndComputeResults(session))}
              >
                {busyAct === 'close' ? 'Closing…' : 'Close & reveal results'}
              </PrimaryButton>
            </div>
          </AppCard>
        ) : null}

        {(session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED') && resultRows.length > 0 ? (
          <AppCard>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Eyebrow>The result</Eyebrow>
              {/* S-6: take the result out — a CSV, or present it on the Board. */}
              <div className="flex items-center gap-2" data-testid="session-result-out">
                <CsvExportButton
                  data={sessionAppResultCsv(session, items, prices)}
                  columns={['Place', 'Option', 'Result']}
                  filename={`${session.name || 'session'}-result`}
                  label="Export CSV"
                />
                <a
                  href={`/prioritization/sessions/${session.id}/tv`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 font-body text-xs text-gray-700 hover:bg-gray-50 dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]"
                >
                  <Tv className="h-3.5 w-3.5" /> Present on the Board
                </a>
              </div>
            </div>
            {resultGroups ? (
              <div className="mt-3 space-y-4" data-testid="session-result-groups">
                {resultGroups.map((group) => (
                  <div key={group.key}>
                    <p className="font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                      {group.label}
                      <span className="ml-2 font-normal text-gray-500 dark:text-[var(--text-ter)]">
                        {group.rows.length} {group.rows.length === 1 ? 'option' : 'options'}
                      </span>
                    </p>
                    <ul className="mt-1 space-y-1 border-l border-gray-200 pl-3 dark:border-[var(--rule)]">
                      {group.rows.map((row) => (
                        <li key={row.id} className="font-body text-sm">
                          <span className="text-gray-900 dark:text-[var(--text-pri)]">{row.name}</span>
                          {row.agreement != null ? (
                            <span className="ml-2 text-xs text-gray-500 dark:text-[var(--text-ter)]">
                              {row.agreement}% agree
                            </span>
                          ) : null}
                          {row.split ? (
                            <span className="block text-xs text-gray-500 dark:text-[var(--text-ter)]">{row.split}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <ol className="mt-3 space-y-2">
                {resultRows.map((row, i) => (
                  <li key={`${row.id}-${i}`} className="flex items-center gap-3">
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-xs ${
                        (ranks[row.id]?.rank ?? i + 1) === 1
                          ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                          : 'bg-gray-100 text-gray-600 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]'
                      }`}
                    >
                      {formatRank(ranks[row.id]?.rank ?? i + 1, ranks[row.id]?.tied ?? false)}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                      {row.name}
                    </span>
                    {row.detail ? (
                      <span className="shrink-0 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                        {row.detail}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            )}
            {session.state === 'RUN_COMPLETE' && !resultGroups ? (
              <div className="mt-4 space-y-4">
                {/* S-4: reveal it on the Board, a rank at a time, before publishing */}
                <div data-testid="host-reveal">
                  <RevealOrchestrator
                    sessionId={session.id}
                    isFacilitator
                    items={resultRows.map((row, i) => ({
                      rank: ranks[row.id]?.rank ?? i + 1,
                      title: row.name,
                      score: session.rankingOutput?.scores?.[row.id]?.total ?? 0,
                    }))}
                  />
                </div>
                <TiePanel
                  sessionId={session.id}
                  rankedItemIds={rankedIds}
                  ties={ties}
                  getItemTitle={nameOf}
                  canBreak={!!rankRule?.canBreakTies}
                  onDone={async () => {
                    await fetchSessionById(session.id);
                    reloadRankRule();
                  }}
                />
              </div>
            ) : null}
            {session.state === 'RUN_COMPLETE' && mayHost ? (
              <div className="mt-4">
                {/* 1001: not drawn for a person the cycle's decision right shuts out; the database's own sentence instead */}
                {refusedByRight(publishRight) ? (
                  <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">{publishRight.line}</p>
                ) : (
                  <>
                    <PrimaryButton disabled={busy} onClick={() => void run('publish', () => publishResults(session.id))}>
                      {busyAct === 'publish' ? 'Publishing…' : 'Publish the result'}
                    </PrimaryButton>
                    <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                      Publishing makes the result part of the record — participants see
                      it, and it can be cited later.
                    </p>
                  </>
                )}
              </div>
            ) : null}
          </AppCard>
        ) : null}

        {error ? (
          <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">{error}</p>
        ) : null}
      </div>
    </AppShell>
  );
}
